import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { DialogDescription } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { isHotkey } from 'is-hotkey';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { Kbd } from '@fabric-app/ui/components/Kbd';
import { P } from '@fabric-app/ui/components/Typography';
import { IconEnter } from '@fabric-app/icons/old';
import { useFormik } from 'formik';
import { TextInput } from '@fabric-app/ui/components/TextInput/TextInput';
import { Flex } from '@fabric-app/ui/components/Flex';
import { InputField } from '@fabric-app/ui/components/Field';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { ModalDrawerContent } from '@fabric-app/ui/components/Modal/Drawer';
import { isString } from '@fabric-app/utils/guards';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { useState } from 'react';

const PromptDisplay = ({ alert }) => {
  const [formRef, setFormRef] = useState(null);
  const { isMobileView } = useResponsive();
  const formik = useFormik({
    initialValues: alert.defaultValue,
    validationSchema: alert.validation,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit: async (values, formik2) => {
      alert.onConfirm(values);
      formik2.setSubmitting(false);
    }
  });
  const onConfirmEnter = (e) => {
    if (!isHotkey("enter", e) || formik.isSubmitting || !formik.isValid) return;
    e.preventDefault();
    alert.onConfirm(formik.values);
  };
  const contentProps = {
    onKeyDown: onConfirmEnter,
    onOpenAutoFocus: () => {
      return true;
    }
  };
  const buttons = /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsxs(Modal.Button, {
        ...alert.confirmButtonProps,
        onClick: () => alert.onConfirm(formik.values),
        tabIndex: 1,
        style: {
          order: 10
        },
        disabled: formik.isSubmitting || !formik.isValid,
        children: [
          alert.confirmLabel,
          " ",
          /* @__PURE__ */ jsx(Kbd, {
            children: /* @__PURE__ */ jsx(IconEnter, {})
          })
        ]
      }),
      /* @__PURE__ */ jsxs(Modal.CancelButton, {
        onClick: alert.onCancel,
        tabIndex: 1,
        children: [
          alert.cancelLabel,
          " ",
          /* @__PURE__ */ jsx(Kbd, {
            children: "ESC"
          })
        ]
      })
    ]
  });
  useRunOnce(() => {
    if (!formRef || !alert.autoFocus) return;
    const focusTarget = isString(alert.autoFocus) ? formRef.querySelector(`input[name="${alert.autoFocus}"]`) : formRef.querySelector("input");
    if (focusTarget && focusTarget instanceof HTMLInputElement) {
      focusTarget.focus();
    }
  }, {
    condition: Boolean(formRef)
  });
  const form = /* @__PURE__ */ jsx("form", {
    onSubmit: formik.handleSubmit,
    ref: setFormRef,
    children: /* @__PURE__ */ jsx(Flex, {
      gap: "sections",
      direction: "column",
      children: /* @__PURE__ */ jsxs(Flex, {
        gap: "sectionsShort",
        direction: "column",
        children: [
          alert.content && /* @__PURE__ */ jsx(P, {
            size: "md",
            children: alert.content
          }),
          Object.entries(alert.defaultValue).map(([key], i) => {
            const label = alert.labels?.[key];
            const placeholder = alert.placeholders?.[key];
            const textInput = /* @__PURE__ */ jsx(TextInput, {
              autoFocus: isString(alert.autoFocus) ? alert.autoFocus === key : alert.autoFocus && i === 0,
              inputHeight: "lg",
              name: key,
              type: "text",
              placeholder,
              value: formik.values[key],
              onChange: formik.handleChange,
              disabled: formik.isSubmitting,
              onBlur: formik.handleBlur,
              tabIndex: 1
            }, `input-${key}`);
            return label ? /* @__PURE__ */ jsx(InputField.Composed, {
              label,
              children: textInput
            }, key) : textInput;
          })
        ]
      })
    })
  });
  if (alert.fullScreen || isMobileView && alert.fullScreenMobile) {
    return /* @__PURE__ */ jsx(Modal.Root, {
      open: true,
      onOpenChange: alert.onCancel,
      children: /* @__PURE__ */ jsx(Modal.Portal, {
        container: getDefaultRadixPortalContainer(),
        children: /* @__PURE__ */ jsxs(Modal.Content, {
          width: "full",
          height: "full",
          dynamicHeight: false,
          ...contentProps,
          children: [
            alert.title && /* @__PURE__ */ jsx(Modal.Header, {
              padding: "sm",
              variant: "default",
              mobileOnly: true,
              children: alert.title
            }),
            alert.content && /* @__PURE__ */ jsx(VisuallyHidden, {
              children: /* @__PURE__ */ jsx(DialogDescription, {
                children: alert.content
              })
            }),
            /* @__PURE__ */ jsx(Modal.Body, {
              padding: "sm",
              style: {
                flexGrow: 1
              },
              children: form
            }),
            /* @__PURE__ */ jsx(Modal.Footer, {
              gap: "lg",
              variant: "fullscreen",
              children: buttons
            })
          ]
        })
      })
    });
  }
  const content = /* @__PURE__ */ jsxs(Modal.Body, {
    padding: "sm",
    style: {
      gap: 20
    },
    children: [
      alert.title && /* @__PURE__ */ jsx(Modal.Title, {
        size: "xs",
        style: {
          marginTop: ".125rem"
        },
        children: alert.title
      }),
      alert.content && /* @__PURE__ */ jsx(VisuallyHidden, {
        children: /* @__PURE__ */ jsx(DialogDescription, {
          children: alert.content
        })
      }),
      form,
      /* @__PURE__ */ jsx(Modal.DoubleButtonContainer, {
        children: buttons
      })
    ]
  });
  return /* @__PURE__ */ jsx(Modal.Root, {
    open: true,
    onOpenChange: alert.onCancel,
    children: /* @__PURE__ */ jsxs(Modal.Portal, {
      container: getDefaultRadixPortalContainer(),
      children: [
        /* @__PURE__ */ jsx(Modal.Overlay, {
          visibleOnMobileViewport: true
        }),
        isMobileView ? /* @__PURE__ */ jsx(ModalDrawerContent, {
          onClose: alert.onCancel,
          deprecatedPadding: true,
          respectContentHeight: true,
          baseHeight: "0dvh",
          children: content
        }) : /* @__PURE__ */ jsx(Modal.Content, {
          width: "xs",
          disableMobileFullscreen: true,
          ...contentProps,
          children: content
        })
      ]
    })
  });
};

export { PromptDisplay as default };
//# sourceMappingURL=PromptDisplay.js.map
